import React from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import { Skeleton } from '@material-ui/lab';
import RedoSearchButton from '../RedoSearchButton';
import { MarkerType } from '../../Marker';
import { ControlPosition } from '../Map.constants';
import MapComponent from '../MainMap';
import DrawerAssembly from './DrawerAssembly';
import messages from './messages';
import lodgeLinkMessages from '../../../constants/messages/lodgeLink';
import PropertyPane from '../../PropertyPane/PropertyPane';

import {
  MapComponentContainer,
  RedoSearchButtonContainer,
  LoadingContainer,
  LoadingDrawer,
  LoadingMap,
  SkeletonContainer
} from './styles';

const withLoader = BaseComponent =>
  function(props) {
    const { loading, ...otherProps } = props;

    return (
      <>
        <BaseComponent {...otherProps} className={loading ? 'loading' : undefined} />
        {loading ? (
          <LoadingContainer>
            <LoadingDrawer>
              <SkeletonContainer>
                {Array.from(Array(8)).map(() => (
                  <Skeleton variant="rect" animation="wave" width="100%" height={125} style={{ marginBottom: 15 }} />
                ))}
              </SkeletonContainer>
            </LoadingDrawer>
            <LoadingMap />
          </LoadingContainer>
        ) : null}
      </>
    );
  };

const {
  publicRuntimeConfig: { GOOGLE_MAPS_API_KEY, SKYBASE_API_KEY }
} = getConfig();

const fixedMapSettings = {
  googleKey: GOOGLE_MAPS_API_KEY,
  maxZoom: 18,
  minZoom: 3,
  skyBaseKey: SKYBASE_API_KEY,
  zoomAnimation: true,
  markerZoomAnimation: true,
  fadeAnimtion: true
};

/**
 * Combines Main Map, MapLayerPanel, FiltersContainer and Drawer into a single component
 */
class MapAssembly extends React.Component {
  static propTypes = {
    properties: PropTypes.arrayOf(PropTypes.shape({})),
    favouriteProperties: PropTypes.arrayOf(PropTypes.shape({})),
    mapSettings: PropTypes.shape({
      center: PropTypes.arrayOf(PropTypes.number),
      zoom: PropTypes.number
    }),
    noResults: PropTypes.bool,
    refetchPropertyResults: PropTypes.func,
    setSearchCriteria: PropTypes.func,
    analytics: PropTypes.shape({}),
    refetchProperties: PropTypes.func,
    setMapFilters: PropTypes.func,
    mapFilters: PropTypes.shape({
      mapLayerPanelState: PropTypes.shape({
        layers: PropTypes.arrayOf(PropTypes.shape({}))
      }),
      filtersContainerState: PropTypes.shape({}),
      filtersContainerProps: PropTypes.shape({})
    }),
    markers: PropTypes.arrayOf(PropTypes.shape({})),
    mapLayers: PropTypes.arrayOf(PropTypes.shape({})),
    intl: PropTypes.shape({}).isRequired,
    router: PropTypes.shape({}),
    propertySearchInput: PropTypes.shape({}),
    propertiesLoading: PropTypes.bool,
    loading: PropTypes.bool,
    propertyCount: PropTypes.number,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
    currentMarkers: PropTypes.arrayOf(PropTypes.shape({})),
    client: PropTypes.shape({}),
    auth: PropTypes.shape({}),
    className: PropTypes.string
  };

  static defaultProps = {
    properties: [],
    favouriteProperties: [],
    mapSettings: {
      center: [53.9333, -116.5765], // Alberta's center
      zoom: 6
    },
    noResults: false,
    refetchPropertyResults: () => {},
    setSearchCriteria: () => {},
    analytics: null,
    refetchProperties: () => {},
    setMapFilters: () => {},
    mapFilters: null,
    markers: [],
    mapLayers: [],
    router: null,
    propertySearchInput: null,
    propertiesLoading: false,
    loading: false,
    propertyCount: 0,
    error: null,
    currentMarkers: [],
    client: null,
    auth: null,
    className: ''
  };

  static getDerivedStateFromProps(props, state) {
    const { mapFilters, propertiesLoading: propLoading } = props;

    if (!mapFilters) {
      return null;
    }

    const { filtersContainerState, mapLayerPanelState, propertiesLoading } = state;

    if (propLoading !== propertiesLoading) {
      return {
        ...state,
        propertiesLoading: propLoading
      };
    }

    if (!filtersContainerState.initializedProps || !mapLayerPanelState.initializedProps) {
      const newState = {};

      if (!filtersContainerState.initializedProps) {
        newState.filtersContainerState = {
          ...filtersContainerState,
          ...mapFilters.filtersContainerState,
          initializedProps: true
        };
      }

      if (!mapLayerPanelState.initializedProps) {
        const { layers } = mapFilters.mapLayerPanelState;

        newState.mapLayerPanelState = {
          ...mapLayerPanelState,
          layers: layers?.map(layer => ({ ...layer })), // Create a copies of object to avoid mutation
          initializedProps: true
        };
      }

      return newState;
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      activeId: null, // id of active PropertyMarkerModel
      savedDetailedMapVisible: false,
      showRedo: false,
      showSelectedPropertyPane: false,
      selectedPropertyPane: null,
      selectedPropertyPaneId: null,
      propertySelectFromMarker: false,
      mapLayerPanelState: {
        detailedLayerButton: {
          layerId: 'detailedLayer',
          label: props.intl.formatMessage(messages.detailedLayerButtonLabel),
          icon: 'map-grid',
          selected: false
        },
        styleOverrides: `
          @media (max-width: 640px) {
            bottom: 75px;
          }
        `,
        panelShowing: false
      },
      filtersContainerState: {
        propertyTypes: [],
        showPanel: false,
        selectedOption: undefined,
        distanceUnitAfter: 'km', // hardcoding distance unit for now
        distanceMin: 50,
        distanceMax: 1000,
        priceRangeSliderMin: 0,
        priceRangeSliderMax: 2000,
        priceRangeStartIcon: '$', // hardcoding currency sign for now
        showSeeMore: true,
        hideInstantBook: true,
        amenities: []
      },
      bookingBypassReasonId: undefined,
      propertiesLoading: false
    };
  }

  shouldComponentUpdate() {
    const { loading } = this.props;

    if (loading || this.loading) {
      return false;
    }

    return true;
  }

  componentWillUnmount() {
    sessionStorage.removeItem('propertyPricingMismatch');
  }

  // Set correct center and zoom on properties change
  componentDidUpdate(prevProps) {
    const { properties } = this.props;

    if (this.redoSearch) {
      if (this.resetRedo) {
        this.redoSearch = false;
        this.resetRedo = false;
      }

      return null;
    }

    if (prevProps.properties !== properties) {
      this.handlePropertyPaneClose();
      if (this.map) {
        const { currentMarkers, analytics } = this.props;

        if (currentMarkers && currentMarkers.length) {
          const center = this.map
            .fitBounds(currentMarkers)
            .invalidateSize()
            .getCenter();

          this.mapSettings = {
            center: [center.lat, center.lng],
            zoom: this.map.getZoom()
          };
        }

        if (this.mapSettings) {
          const { center, zoom } = this.mapSettings;

          analytics.trackEvent('result-listing', {
            sectionName: 'Search Results Listing',
            searchResultsListing: 'properties',
            returnedPropertyNumber: properties.length,
            mapPosition: `${center ? center[0] : 'unknown'}, ${center ? center[1] : 'unknown'}`,
            mapZoom: zoom
          });
        }
      }
    }

    return null;
  }

  handleRedoSearch = () => {
    const { refetchPropertyResults } = this.props;
    this.handlePropertyPaneClose();
    const { setSearchCriteria, analytics } = this.props;
    this.redoSearch = true; // We want to use existing zoom and centers for map

    this.setState({ showRedo: false }, async () => {
      if (this.map) {
        this.loading = true;
        const centerLatLng = this.map.getCenter();
        const mapZoom = this.map.getZoom();

        await Promise.all([
          setSearchCriteria({
            type: 'updateSearchLocationOnRedo',
            payload: {
              locationValue: `${centerLatLng.lat.toFixed(4)}, ${centerLatLng.lng.toFixed(4)}`,
              locationType: 'LatLng'
            }
          }),
          setSearchCriteria({
            type: 'commitSearchTerm'
          })
        ]);

        this.forceUpdate(() => {
          this.loading = false;
          this.resetRedo = true;
        });

        await refetchPropertyResults();

        analytics.trackEvent('redo-search', {
          sectionName: 'Search Results Map',
          mapPosition: `${centerLatLng.lat}, ${centerLatLng.lng}`,
          mapZoom
        });
      }
    });
  };

  openOnNew = slug => {
    const {
      router,
      propertySearchInput: { checkin, checkout, numberRooms }
    } = this.props;

    const { bookingBypassReasonId } = this.state;

    let href = router.getRouteUrl('PropertyDetails', {
      slug
    });

    if (checkin) {
      href += `?checkin=${checkin}&checkout=${checkout}`;
      if (numberRooms) {
        href += `&numRooms=${numberRooms}`;
      }
      // if gdsBypass is selected when it's CX, append it in the URL...
      if (bookingBypassReasonId) {
        href += `&bookingBypassReasonId=${bookingBypassReasonId}`;
      }
    }
    sessionStorage.removeItem('propertyPricingMismatch'); // Remove the propertyPricingMismatch in session storage when user clicks on view details so we do not trigger analytics
    window.open(href, '_blank');
  };

  handlePropertyPaneOpen = property => {
    const { slug } = property;
    const {
      propertySearchInput: { checkin, checkout }
    } = this.props;

    this.setState({
      propertySelectFromMarker: false,
      showSelectedPropertyPane: true,
      selectedPropertyPane: property,
      selectedPropertyPaneId: property.id
    });
    setTimeout(() => {
      this.setState({
        activeId: property.id,
        activeModel: property
      });
    }, 300);

    const bookingPriceDetails = {
      organizationSlug: slug,
      checkin,
      checkout,
      property
    };

    typeof window !== 'undefined' &&
      window.sessionStorage.setItem('propertyPricingMismatch', JSON.stringify(bookingPriceDetails));
  };

  handlePropertyPaneClose = () => {
    this.setState({
      showSelectedPropertyPane: false,
      selectedPropertyPane: null,
      selectedPropertyPaneId: null,
      propertySelectFromMarker: false,
      activeId: null
    });
    this.handlePopupClose();
    this.forceUpdate();
  };

  handleDragEnd = () => {
    this.setState({
      showRedo: true
    });
  };

  handleMoveEnd = () => {
    if (this.map && this.mapSettings) {
      this.mapSettings = {
        center: this.map.center,
        zoom: this.map.zoom
      };
    }
    this.setState({
      showRedo: true
    });
  };

  handlePropertySelectFromMarker = () => {
    this.setState({
      propertySelectFromMarker: true
    });
  };

  handleMarkerClick = model => {
    const { slug } = model;
    const { propertySelectFromMarker, selectedPropertyPaneId } = this.state;
    const {
      propertySearchInput: { checkin, checkout }
    } = this.props;
    if (!this.popupClosed) {
      this.setState({
        showSelectedPropertyPane: true,
        selectedPropertyPane: model
      });
      if (propertySelectFromMarker && selectedPropertyPaneId !== null) {
        this.setState({
          selectedPropertyPaneId: null
        });
      }
      setTimeout(() => {
        this.setState({
          activeId: model.id,
          activeModel: model
        });
      }, 300);
      const bookingPriceDetails = {
        organizationSlug: slug,
        checkin,
        checkout,
        property: model
      };
      typeof window !== 'undefined' &&
        window.sessionStorage.setItem('propertyPricingMismatch', JSON.stringify(bookingPriceDetails));
    }

    if (model) {
      const { analytics } = this.props;

      if (model.type === MarkerType.PROPERTY) {
        analytics.trackEvent('lodge-click-pin', {
          sectionName: 'Search Results Map',
          lodgeName: model.label,
          location: model.description,
          favorited: model.favourite,
          badge: ''
        });
      } else {
        analytics.trackEvent('map-poi-click', {
          sectionName: 'Search Results Map',
          pointOfInterest: model.label
        });
      }
    }
  };

  handlePopupOpen = () => {
    this.popupClosed = false;
  };

  handlePopupClose = () => {
    this.popupClosed = true;
    this.setState({
      activeId: null,
      activeModel: null,
      showSelectedPropertyPane: false,
      selectedPropertyPane: null,
      selectedPropertyPaneId: null,
      propertySelectFromMarker: false
    });
    this.forceUpdate();
  };

  handlePopupImageClick = () => {
    const { activeModel } = this.state;

    if (activeModel) {
      const { analytics } = this.props;

      analytics.trackEvent('lodge-click', {
        sectionName: 'Search Results Listing',
        lodgeName: activeModel.label,
        location: activeModel.description
      });

      this.openOnNew(activeModel.slug);
    }
  };

  setMap = map => {
    this.map = map.target;
  };
  /* ---------------------------------------- Main Map functions End ---------------------------------------- */

  /* ---------------------------------------- Drawer Functions Start ---------------------------------------- */

  /**
   * @param orderby - type; Alphabetical, Distance, Price,
   * @param order - ascending (ASC) or descending (DESC)
   */
  refetchOnSort = async (orderby, order) => {
    const { refetchProperties, setMapFilters } = this.props;

    if (refetchProperties) {
      this.loading = true;

      if (orderby === 'Relevance') {
        await refetchProperties(null, {
          orderby: null,
          order: null
        });

        this.loading = false;
        this.forceUpdate();
      } else {
        setMapFilters({
          type: 'updateSort',
          payload: {
            orderBy: orderby,
            order
          }
        });
      }
    }
  };

  buildDrawerProps = () => {
    const { intl, properties, propertyCount, noResults, error, favouriteProperties } = this.props;
    const { activeId, filtersContainerState, propertiesLoading } = this.state;

    if (this.drawerProps) {
      const { hide, currentActive } = this.drawerProps;

      const condition =
        hide === filtersContainerState.showPanel &&
        currentActive === activeId &&
        this.drawerProps.properties === properties &&
        this.drawerProps.intl === intl;

      if (condition) {
        return this.drawerProps;
      }
    }

    this.drawerProps = {
      intl,
      noResults,
      loading: propertiesLoading,
      error: error && true,
      hide: false, // filtersContainerState.showPanel,
      refetchOnSort: this.refetchOnSort,
      properties,
      totalProperties: propertyCount,
      startingAtLabel: intl.formatMessage(messages.startingAtLabel),
      priceFrequencyLabel: intl.formatMessage(messages.priceFrequencyLabel),
      callForPricingLabel: intl.formatMessage(messages.callForPricingLabel),
      callForAvailability: intl.formatMessage(messages.callForAvailability),
      openText: !error
        ? intl.formatMessage(messages.openText, { totalProperties: propertyCount || 0 })
        : intl.formatMessage(messages.openTextError),
      closeText: intl.formatMessage(messages.closeText),
      drawerSelectProps: {
        options: intl.formatMessage(messages.options, { LtH: '⇡', HtL: '⇣' }).split(','),
        selectLabel: intl.formatMessage.selectLabel,
        propertiesLabel: intl.formatMessage(messages.propertiesLabel, { currentProperties: properties.length })
      },
      currentActive: activeId,
      setActive: id => {
        this.setState({ activeId: id });
      },
      openOnNew: this.openOnNew,
      onDrawerToggle: this.handletoggleDrawer,
      ref: drawer => {
        // eslint-disable-next-line
        this.drawer = drawer;
      },
      noResultsProps: {
        title: intl.formatMessage(messages.noResultsTitle),
        subtitle: intl.formatMessage(messages.noResultsSubtitle),
        phone: intl.formatMessage(lodgeLinkMessages.phoneNumber),
        suggestionLabel: intl.formatMessage(messages.suggestionLabel),
        suggestionList: intl.formatMessage(messages.suggestionList)
      },
      errorResultsProps: error && {
        title: intl.formatMessage(messages.errorResultsTitle),
        description: intl.formatMessage(messages.errorResultsDescription),
        phone: intl.formatMessage(lodgeLinkMessages.phoneNumber)
      },
      favouriteProperties
    };

    return this.drawerProps;
  };

  handletoggleDrawer = () => {
    if (this.toggleDrawerTimer) {
      clearTimeout(this.toggleDrawerTimer);
    }

    this.toggleDrawerTimer = setTimeout(() => {
      this.map.invalidateSize();
    }, 250);
  };
  /* ---------------------------------------- Drawer Functions End ---------------------------------------- */

  /* ---------------------------------------- FiltersContainer Handlers Start ----------------------------------- */

  buildFiltersContainerProps = () => {
    const { mapFilters } = this.props;
    const filterLabels = this.getFilterLabels;

    return {
      mapFilters,
      filterLabels
    };
  };

  getFilterLabels = () => {
    const { mapFilters, intl } = this.props;

    if (!mapFilters) {
      return undefined;
    }

    const {
      filtersContainerState: { distance }
    } = this.state;

    const {
      filtersContainerProps: { numRooms, labelStartDate, labelEndDate, location }
    } = mapFilters;
    return {
      labels: {
        buttonFilterTemplate: intl.formatMessage(messages.buttonFilterTemplate, { numFilters: 0 }),
        buttonRoomsTemplate: intl.formatMessage(messages.buttonRoomsTemplate, { numRooms }),
        buttonDatesTemplate: intl.formatMessage(messages.buttonDatesTemplate, { labelStartDate, labelEndDate }),
        startDate: intl.formatMessage(messages.startDate),
        endDate: intl.formatMessage(messages.endDate),
        dates: intl.formatMessage(messages.dates),
        apply: intl.formatMessage(messages.apply),
        reset: intl.formatMessage(messages.reset),
        headerDates: intl.formatMessage(messages.headerDates),
        datesDisclaimer: intl.formatMessage(messages.datesDisclaimer),
        rooms: intl.formatMessage(messages.rooms),
        headerRooms: intl.formatMessage(messages.headerRooms),
        roomsDescription: intl.formatMessage(messages.roomsDescription),
        roomsButtonLabel: intl.formatMessage(messages.roomsButtonLabel),
        roomsDisclaimerMobile: intl.formatMessage(messages.roomsDisclaimerMobile),
        roomsDisclaimerDesktop: intl.formatMessage(messages.roomsDisclaimerDesktop),
        headerFilter: intl.formatMessage(messages.headerFilter),
        propertyTypeTitle: intl.formatMessage(messages.propertyTypeTitle),
        propertyTypesDescription: intl.formatMessage(messages.propertyTypesDescription),
        distanceTitle: intl.formatMessage(messages.distanceTitle),
        distanceDescriptionTemplate: intl.formatMessage(messages.distanceDescriptionTemplate, { distance, location }),
        priceRangeTitle: intl.formatMessage(messages.priceRangeTitle),
        priceRangeDescription: intl.formatMessage(messages.priceRangeDescription),
        // eslint-disable-next-line no-template-curly-in-string
        priceRangeNumberFormat: '${price}',
        amenitiesTitle: intl.formatMessage(messages.amenitiesTitle),
        amenitiesDescription: intl.formatMessage(messages.amenitiesDescription),
        seeMoreLabel: intl.formatMessage(messages.seeMoreLabel),
        instantBookTitle: intl.formatMessage(messages.instantBookTitle),
        instantBookDescription: intl.formatMessage(messages.instantBookDescription),
        clearButtonLabel: intl.formatMessage(messages.clearButtonLabel),
        updateButtonLabel: intl.formatMessage(messages.updateButtonLabel)
      }
    };
  };

  // Callback function to receive data from the child
  handleSelectedBypassReason = data => {
    this.setState({ bookingBypassReasonId: data });
  };

  /* ---------------------------------------- FiltersContainer Handlers End ----------------------------------- */

  render() {
    const {
      savedDetailedMapVisible,
      showRedo,
      mapLayerPanelState,
      filtersContainerState,
      selectedPropertyPane,
      showSelectedPropertyPane,
      selectedPropertyPaneId,
      propertySelectFromMarker,
      bookingBypassReasonId
    } = this.state;
    const {
      markers,
      mapLayers,
      analytics,
      className,
      intl,
      client,
      auth,
      setMapFilters,
      setSearchCriteria
    } = this.props;
    const { mapSettings } = this.props;
    const drawerProps = this.buildDrawerProps();
    const filterProps = this.buildFiltersContainerProps();

    return (
      <MapComponentContainer
        id="MapComponentContainer"
        className={className}
        panelShowing={mapLayerPanelState.panelShowing || filtersContainerState.showPanel}
      >
        <DrawerAssembly
          key={`DrawerAssembly ${showSelectedPropertyPane}`}
          {...drawerProps}
          {...filterProps}
          setMapFilters={setMapFilters}
          setSearchCriteria={setSearchCriteria}
          filtersContainerState={filtersContainerState}
          analytics={analytics}
          auth={auth}
          client={client}
          bookingBypassReasonId={this.handleSelectedBypassReason}
          handlePropertyPaneOpen={this.handlePropertyPaneOpen}
          handlePropertyPaneClose={this.handlePropertyPaneClose}
          showSelectedPropertyPane={showSelectedPropertyPane}
        />
        <PropertyPane
          showSelectedPropertyPane={showSelectedPropertyPane}
          selectedPropertyPane={selectedPropertyPane}
          openOnNew={this.openOnNew}
          handlePropertyPaneClose={this.handlePropertyPaneClose}
          intl={intl}
          auth={auth}
          bookingBypassReasonId={bookingBypassReasonId}
        />
        <div className="MainMapContainer">
          <MapComponent
            {...this.mapSettings || mapSettings}
            {...fixedMapSettings}
            markers={markers || []}
            mapLayers={mapLayers || []}
            detailedMapVisible={savedDetailedMapVisible}
            detailedMapOpacity={0.5}
            zoomPosition={ControlPosition.TOP_LEFT}
            zoomControl
            onMoveEnd={this.handleMoveEnd}
            onDragEnd={this.handleDragEnd}
            whenReady={this.setMap}
            onPopupClose={this.handlePopupClose}
            onPopupOpen={this.handlePopupOpen}
            onPropertyPopupImageClick={this.handlePopupImageClick}
            onPropertyPopupNameClick={this.handlePopupImageClick}
            onMarkerClick={this.handleMarkerClick}
            selectedPropertyId={selectedPropertyPaneId}
            focusedMarkerId={selectedPropertyPane?.id}
            handlePropertySelectFromMarker={this.handlePropertySelectFromMarker}
            propertySelectFromMarker={propertySelectFromMarker}
          />
          <RedoSearchButtonContainer show={showRedo}>
            <RedoSearchButton onClick={this.handleRedoSearch} />
          </RedoSearchButtonContainer>
        </div>
      </MapComponentContainer>
    );
  }
}

const MapAssemblyContainer = withLoader(MapAssembly);

export default MapAssemblyContainer;
